import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../store/authContext';
import { orgSettingsApi } from '../../services/api';
import { LockIcon } from '../../utils/icons';
import ChangePasswordModal from '../../components/ChangePasswordModal';
import './Settings.css';

export default function Settings() {
  const { user, logout } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      setError('');
      const res = await orgSettingsApi.get();
      setSettings(res.data);
    } catch {
      setError('Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleToggleAutoAssign = async () => {
    if (!settings) return;
    try {
      setError('');
      const res = await orgSettingsApi.update({ auto_assign_leads: !settings.auto_assign_leads });
      setSettings(res.data);
    } catch {
      setError('Failed to update setting.');
    }
  };

  if (loading) {
    return (
      <div className="os-page">
        <div className="os-loading">
          <div className="os-loading-spinner" />
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="os-page">
      {/* Header */}
      <div className="page-header animate-fade-up">
        <div>
          <p className="section-label" style={{ marginBottom: 4 }}>Configuration</p>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">{user?.tenant_name || 'Organisation'} settings</p>
        </div>
      </div>

      {error && <div className="os-error animate-fade-up">{error}</div>}

      {settings && (
        <>
          {/* Lead Assignment Card */}
          <div className="os-card card animate-fade-up" style={{ animationDelay: '40ms' }}>
            <h2 className="os-card-title">Lead Assignment</h2>
            <div className="os-setting-row">
              <div className="os-setting-info">
                <span className="os-setting-label">Automatic lead assignment</span>
                <span className="os-setting-desc">
                  {settings.auto_assign_leads
                    ? 'New leads are automatically distributed across active team members using round-robin'
                    : 'New leads are assigned to you (admin)'}
                </span>
              </div>
              <button
                className={`toggle-switch ${settings.auto_assign_leads ? 'toggle-switch--on' : ''}`}
                onClick={handleToggleAutoAssign}
                type="button"
              >
                <span className="toggle-thumb" />
              </button>
            </div>
          </div>

          {/* Organisation Info Card */}
          <div className="os-card card animate-fade-up" style={{ animationDelay: '80ms' }}>
            <h2 className="os-card-title">Organisation</h2>
            <div className="os-info-grid">
              <div className="os-info-row">
                <span className="os-info-label">Max users</span>
                <span className="os-info-value">{settings.max_users}</span>
              </div>
              <div className="os-info-row">
                <span className="os-info-label">Leads module</span>
                <span className="os-info-value">
                  <span className={`badge badge-${settings.leads_module_enabled ? 'closed' : 'lost'}`}>
                    <span className="badge-dot" />
                    {settings.leads_module_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </span>
              </div>
              <div className="os-info-row">
                <span className="os-info-label">Booking module</span>
                <span className="os-info-value">
                  <span className={`badge badge-${settings.booking_module_enabled ? 'closed' : 'lost'}`}>
                    <span className="badge-dot" />
                    {settings.booking_module_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </span>
              </div>
              <div className="os-info-row">
                <span className="os-info-label">Sales module</span>
                <span className="os-info-value">
                  <span className={`badge badge-${settings.sales_module_enabled ? 'closed' : 'lost'}`}>
                    <span className="badge-dot" />
                    {settings.sales_module_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* Your Access Card — for admin users with module assignment */}
          {user?.role === 'admin' && user?.assignedModules && (
            <div className="os-card card animate-fade-up" style={{ animationDelay: '100ms' }}>
              <h2 className="os-card-title">Your Access</h2>
              <div className="os-info-grid">
                <div className="os-info-row">
                  <span className="os-info-label">Module access</span>
                  <span className="os-info-value">
                    {(user.assignedModules || []).map((m) => (
                      <span key={m} className={`module-badge module-badge--${m}`} style={{ marginRight: 4 }}>
                        {m.charAt(0).toUpperCase() + m.slice(1)}
                      </span>
                    ))}
                  </span>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Account Actions Card */}
      <div className="os-card card animate-fade-up" style={{ animationDelay: '120ms' }}>
        <h2 className="os-card-title">Account</h2>
        <div className="os-account-actions">
          <button
            className="btn btn-ghost os-account-btn"
            onClick={() => setShowChangePassword(true)}
          >
            <LockIcon size={14} />
            Change Password
          </button>
          <button className="btn btn-danger os-account-btn" onClick={logout}>
            <LogoutIcon size={14} />
            Sign Out
          </button>
        </div>
      </div>

      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
    </div>
  );
}

function LogoutIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M6 2H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <path d="M10.5 11L14 8l-3.5-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M14 8H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}
